import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import {
  CalendarBlankIcon,
  DotsSixVerticalIcon,
  GraduationCapIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import type { Education } from "@reactive-resume/schema";

import { formatEducationPeriod, plainText } from "./date-utils";

type Props = { item: Education; onEdit: (item: Education) => void; onDelete: (id: string) => void };

export const EducationCard = ({ item, onEdit, onDelete }: Props) => {
  const sortable = useSortable({ id: item.id });
  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.55 : 1,
      }}
      className="grid grid-cols-[28px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_7px_22px_rgba(15,23,42,0.04)]"
    >
      <button
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
        aria-label="Réorganiser cette formation"
        className="grid cursor-grab place-items-center border-r border-slate-100 text-slate-300 hover:bg-indigo-50 hover:text-indigo-500"
      >
        <DotsSixVerticalIcon size={14} weight="bold" />
      </button>
      <div className="min-w-0 p-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 gap-2.5">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-violet-50 text-violet-700">
              <GraduationCapIcon size={17} weight="duotone" />
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-xs font-bold text-indigo-950 sm:text-sm">
                {item.studyType || "Diplôme non renseigné"}
              </h3>
              <p className="mt-0.5 truncate text-[11px] font-semibold text-indigo-600">
                {item.institution || "Établissement non renseigné"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0">
            <button
              type="button"
              onClick={() => onEdit(item)}
              aria-label="Modifier"
              className="grid size-8 place-items-center rounded-lg text-indigo-600 hover:bg-indigo-50"
            >
              <PencilSimpleIcon size={15} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              aria-label="Supprimer"
              className="grid size-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            >
              <TrashIcon size={15} />
            </button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[9px] text-slate-500 sm:text-[10px]">
          <span className="inline-flex items-center gap-1">
            <CalendarBlankIcon size={12} />
            {formatEducationPeriod(
              item.startDate ?? "",
              item.endDate ?? "",
              Boolean(item.isPresent),
            ) ||
              item.date ||
              "Période non renseignée"}
          </span>
          {item.area && <span>{item.area}</span>}
          {item.score && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
              {item.score}
            </span>
          )}
        </div>
        {plainText(item.summary) && (
          <p className="mt-2 line-clamp-2 text-[10px] leading-4 text-slate-600">
            {plainText(item.summary)}
          </p>
        )}
      </div>
    </article>
  );
};
